/**
 * API client เดียวของ SPA — BFF cookie session, ไม่มี token ฝั่ง browser
 * - credentials: include (cookie __Host-sess)
 * - X-Requested-With: uwu-web (Worker ใช้เป็นสัญญาณ CSRF)
 * - 401 → ส่งไป /login?next=…
 * - error body ตาม ErrorResponse
 */
import { ErrorResponse } from "@uwu/contracts";
import { authLoginHref, currentLang } from "./i18n";

export type ErrorDetail = { path: string; message: string };

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ErrorDetail[];
  readonly requestId: string | undefined;
  constructor(status: number, code: string, message: string, details: ErrorDetail[] = [], requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

export function isApiError(e: unknown, status?: number): e is ApiError {
  return e instanceof ApiError && (status === undefined || e.status === status);
}

export function currentPath(): string {
  return window.location.pathname + window.location.search;
}

export function loginUrl(next: string = currentPath()): string {
  return "/login?next=" + encodeURIComponent(next);
}

let onUnauthorized: (next: string) => void = (next) => {
  window.location.href = loginUrl(next);
};

/** ใช้ในเทสต์/embed เพื่อแทนที่พฤติกรรม redirect */
export function setUnauthorizedHandler(fn: (next: string) => void): void {
  onUnauthorized = fn;
}

/**
 * R4 re-auth interceptor (13 §1 / W3-CONTRACTS §9) — 403 `reauth_required` (สิทธิ์สูงของ admin)
 * ไม่มี default listener (ต่าง 401): แค่แจ้ง subscriber (ReauthDialog) ให้เปิด dialog ด้วย login_url
 * ที่รับประกันว่ามี `next=<path ปัจจุบัน>` (server อาจไม่ใส่มาให้) — ผู้ใช้กดยืนยันแล้วไปหน้า login เอง
 * กลับมาแล้ว "ทำรายการซ้ำ" (ไม่ auto-retry — full page nav ทำให้ state เดิมหายอยู่แล้ว)
 */
type ReauthListener = (loginUrl: string) => void;
let reauthListeners: ReauthListener[] = [];

export function onReauthRequired(fn: ReauthListener): () => void {
  reauthListeners.push(fn);
  return () => {
    reauthListeners = reauthListeners.filter((l) => l !== fn);
  };
}

function withNext(url: string): string {
  if (url.includes("next=")) return url;
  const sep = url.includes("?") ? "&" : "?";
  return url + sep + "next=" + encodeURIComponent(currentPath());
}

/** [T-090 · #255] ลิงก์ /auth/login ของ worker (fallback หรือ server ให้มา) → เติม lang ปัจจุบันของ UI ถ้ายังไม่มี
 *  URL อื่น (เช่น /sso/step-up จาก server) ไม่แตะ — กันส่ง param ไป endpoint ที่ไม่รู้จัก */
function withLang(url: string): string {
  if (!url.startsWith("/auth/login") || url.includes("lang=")) return url;
  return url + (url.includes("?") ? "&" : "?") + "lang=" + currentLang();
}

function notifyReauthRequired(rawLoginUrl: string): void {
  const url = withNext(withLang(rawLoginUrl));
  for (const l of reauthListeners) l(url);
}

export type JsonInit = Omit<RequestInit, "body"> & { json?: unknown };

export async function fetchJson<T>(path: string, init: JsonInit = {}): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set("X-Requested-With", "uwu-web");
  headers.set("Accept", "application/json");
  let body: BodyInit | undefined;
  if (json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(json);
  }

  const res = await fetch(path, { ...rest, headers, body, credentials: "include" });

  if (res.status === 401) {
    onUnauthorized(currentPath());
    throw new ApiError(401, "unauthenticated", "Unauthenticated");
  }
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    const parsed = ErrorResponse.safeParse(data);
    if (parsed.success) {
      const e = parsed.data.error;
      if (res.status === 403 && e.code === "reauth_required") {
        const loginUrlDetail = (e.details ?? []).find((d) => d.path === "login_url")?.message;
        notifyReauthRequired(loginUrlDetail || authLoginHref({ reauth: true }));
      }
      throw new ApiError(res.status, e.code, e.message, e.details ?? [], e.request_id);
    }
    throw new ApiError(res.status, "http_" + res.status, res.statusText || "Request failed");
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, init?: JsonInit) => fetchJson<T>(path, { ...init, method: "GET" }),
  post: <T>(path: string, json?: unknown, init?: JsonInit) => fetchJson<T>(path, { ...init, method: "POST", json }),
  put: <T>(path: string, json?: unknown, init?: JsonInit) => fetchJson<T>(path, { ...init, method: "PUT", json }),
  patch: <T>(path: string, json?: unknown, init?: JsonInit) => fetchJson<T>(path, { ...init, method: "PATCH", json }),
  del: <T>(path: string, json?: unknown, init?: JsonInit) => fetchJson<T>(path, { ...init, method: "DELETE", json }),
};

/** query string จาก object โดยตัด undefined/null/"" · array = คีย์เดิมซ้ำตามสมาชิก (status_type=a&status_type=b) [UX-D1] */
export function qs(params: Record<string, string | number | string[] | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? "?" + s : "";
}
